import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NewsRow } from "@/components/feature/NewsRow";
import { news } from "@/mocks/news";
import { specials } from "@/mocks/specials";
import { searchNews } from "@/utils/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 5;

export default function NewsList() {
  const [input, setInput] = useState("");
  const [keyword, setKeyword] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  usePageMeta({
    title: "资讯动态 - 日本伦理影视行业动态与深度解析",
    description:
      "日本伦理影视行业动态、剧集观察、文化解析与深度访谈。以编辑视角记录伦理题材的叙事转向、产业变化与创作方法，帮助读者理解作品背后的文化语境。",
    keywords: "日本伦理影视资讯,伦理剧观察,日本影视文化,剧情解析,行业动态",
  });

  const sortedNews = useMemo(() => [...news].sort((a, b) => b.date.localeCompare(a.date)), []);
  const results = useMemo(
    () => (keyword ? searchNews(keyword).sort((a, b) => b.date.localeCompare(a.date)) : sortedNews),
    [keyword, sortedNews],
  );

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [keyword]);

  const visibleNews = results.slice(0, visibleCount);
  const hasMore = visibleCount < results.length;

  return (
    <>
      <section className="border-b border-background-800 bg-background-900">
        <div className="mx-auto max-w-[1440px] px-4 pb-10 pt-24 md:px-12 md:pb-12 md:pt-32">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">资讯动态</span>
          </nav>
          <h1 className="mt-5 text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
            资讯动态与深度解析
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            记录日本伦理影视的行业变化与创作方法：剧集观察、文化解析、导演访谈与编辑笔记，每周更新。
          </p>

          <form
            className="mt-6 flex w-full max-w-xl items-center gap-2 rounded-md border border-background-700 bg-background-950 px-3"
            onSubmit={(event) => {
              event.preventDefault();
              setKeyword(input.trim());
            }}
          >
            <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              name="news_search"
              placeholder="搜索资讯标题、栏目或标签"
              className="h-11 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
            {input && (
              <button
                type="button"
                onClick={() => {
                  setInput("");
                  setKeyword("");
                }}
                className="text-foreground-500 transition-colors hover:text-foreground-900"
                aria-label="清空搜索关键词"
              >
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            )}
            <button
              type="submit"
              className="shrink-0 border-l border-background-700 py-1.5 pl-3 text-xs text-accent-400"
            >
              搜索
            </button>
          </form>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-10 md:px-12 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="flex items-center justify-between border-b border-background-800 pb-4">
              <h2 className="text-lg font-semibold text-foreground-950">
                {keyword ? `「${keyword}」的搜索结果` : "全部资讯"}
              </h2>
              <span className="text-xs text-foreground-500">共 {results.length} 篇</span>
            </div>

            {visibleNews.length > 0 ? (
              <>
                <div>
                  {visibleNews.map((item) => (
                    <NewsRow key={item.id} item={item} />
                  ))}
                </div>
                <div className="mt-8 flex flex-col items-center gap-3">
                  <p className="text-xs text-foreground-500">
                    已显示 {visibleNews.length} / {results.length} 篇
                  </p>
                  {hasMore ? (
                    <button
                      type="button"
                      onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                      className="flex items-center gap-2 rounded-md border border-background-600 px-6 py-2.5 text-sm text-foreground-800 transition-colors hover:border-accent-500 hover:text-accent-400"
                    >
                      <i className="ri-add-line" aria-hidden="true"></i>
                      <span className="whitespace-nowrap">加载更多资讯</span>
                    </button>
                  ) : (
                    <p className="text-xs text-foreground-500">已到达列表末尾</p>
                  )}
                </div>
              </>
            ) : (
              <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-700 bg-background-900/60 px-6 py-14 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full border border-background-700 text-foreground-500">
                  <i className="ri-file-search-line text-2xl" aria-hidden="true"></i>
                </span>
                <h3 className="mt-5 text-base font-medium text-foreground-950">
                  没有找到相关资讯
                </h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
                  试着缩短关键词，或浏览全部资讯。当前收录 10 篇行业动态与深度解析。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setInput("");
                    setKeyword("");
                  }}
                  className="mt-6 flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-foreground-950 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line" aria-hidden="true"></i>
                  <span className="whitespace-nowrap">重置搜索</span>
                </button>
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-800 bg-background-900 p-5">
              <h2 className="text-sm font-medium text-foreground-950">栏目分类</h2>
              <ul className="mt-4 space-y-2.5">
                {Array.from(new Set(news.map((item) => item.category))).map((category) => (
                  <li
                    key={category}
                    className="flex items-center justify-between text-sm text-foreground-600"
                  >
                    <span>{category}</span>
                    <span className="font-label text-xs text-foreground-500">
                      {news.filter((item) => item.category === category).length}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 rounded-lg border border-background-800 bg-background-900 p-5">
              <h2 className="text-sm font-medium text-foreground-950">近期专题</h2>
              <ul className="mt-4 space-y-4">
                {specials.map((special) => (
                  <li key={special.id}>
                    <Link to="/special" className="group block">
                      <p className="text-sm text-foreground-700 transition-colors group-hover:text-accent-400">
                        {special.title}
                      </p>
                      <p className="mt-1 text-xs text-foreground-500">{special.subtitle}</p>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/special"
                className="mt-5 flex items-center justify-between rounded-md border border-background-700 px-4 py-2.5 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-400"
              >
                浏览全部专题
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}